import { FunctionComponent, useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  rucksack,
  Typography,
} from '@black-bear-energy/rucksack'
import { LoopRounded, UploadFile } from '@mui/icons-material'
import { TextWithTooltip } from '../common/TextWithTooltip'
import { CHECKLIST_COLUMN_WIDTHS } from '../helpers/DocumentExchange/tableConstants'
import { useUploadChecklistDocument } from '../../api/hooks/useUploadChecklistDocument'
import { maxDocumentUploadSizeMB } from '../../utils/file'

export const InlineFileUploader: FunctionComponent<InlineFileUploaderProps> = ({
  documentId,
  documentName,
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const { mutate: uploadChecklistDocument } = useUploadChecklistDocument()

  // We want to cover all items in the row except for the document name.
  // Calculate what the left inset needs to be, based on the widths of all columns.
  const colWidths = CHECKLIST_COLUMN_WIDTHS.reduce(
    (acc, currCol) => {
      if (currCol === 'unset') {
        acc.unsetColCount += 1
        return acc
      }
      // parse the pixel widths and add to the total
      const colWidth = parseInt(currCol.substring(0, currCol.length - 2))
      acc.totalStaticWidth += colWidth
      return acc
    },
    { totalStaticWidth: 0, unsetColCount: 0 }
  )
  const rowCoverLeftInset = `calc((100% - ${colWidths.totalStaticWidth}px) / ${colWidths.unsetColCount})`

  /** Convert a FileList to File[] */
  const getFileArray = (fileList: FileList): File[] => {
    const fileArray: File[] = []
    for (let i = 0; i < fileList.length; i++) {
      fileArray.push(fileList[i])
    }
    return fileArray
  }

  const handleDrag = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (event.type === 'dragenter' || event.type === 'dragover') {
      setIsDragging(true)
    } else if (event.type === 'dragleave') {
      setIsDragging(false)
    }
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
    const files = getFileArray(event.dataTransfer.files)
    setSelectedFiles(files)
    uploadChecklistDocument(
      {
        documentId,
        documentName,
        files,
      },
      {
        // Revert to the drop zone if the upload fails, so the "Uploading" spinner
        // doesn't get stuck showing after the error toast fires.
        onError: () => setSelectedFiles([]),
      }
    )
  }

  const filesSelected = selectedFiles.length > 0

  return (
    <Box
      sx={{
        height: isDragging ? '100px' : '35px',
        transition: 'height 180ms ease',
      }}
    >
      {filesSelected ? (
        <>
          <Box
            sx={{
              backgroundColor: rucksack.palette.background.card,
              position: 'absolute',
              inset: `1px 1px 1px ${rowCoverLeftInset}`,
              zIndex: 1,
            }}
          />
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              backgroundColor: 'rgba(73, 173, 214, 0.12)',
              position: 'absolute',
              inset: '0px',
              zIndex: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: '1 0 0',
                marginLeft: rowCoverLeftInset,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  height: '24px',
                  alignItems: 'flex-start',
                  paddingLeft: '24px',
                }}
              >
                <Chip
                  icon={
                    <LoopRounded
                      style={{ color: rucksack.palette.action.active }}
                    />
                  }
                  variant="outlined"
                  label="Uploading"
                  size="small"
                  sx={{ color: rucksack.palette.text.secondary }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  padding: '8px 16px',
                  justifyContent: 'center',
                  flex: '1 0 0',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                <UploadFile sx={{ color: rucksack.palette.primary.main }} />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    paddingRight: '100px',
                  }}
                >
                  <Box sx={{ maxWidth: '450px' }}>
                    <TextWithTooltip
                      variant="subtitle1"
                      text={selectedFiles.map((file) => file.name).join(', ')}
                    />
                  </Box>
                </Box>
                <CircularProgress />
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            padding: '20px 16px',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            alignSelf: 'stretch',
            border: isDragging ? '1px dashed rgba(73, 173, 214, 0.50)' : 'none',
            backgroundColor: isDragging ? 'rgba(73, 173, 214, 0.12)' : 'none',
            position: 'absolute',
            inset: '0px',
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              display: isDragging ? 'unset' : 'none',
              pointerEvents: 'none',
            }}
          >
            Drag & Drop File(s) Here to Upload
          </Typography>
          <Typography
            variant="body2"
            sx={{
              display: isDragging ? 'unset' : 'none',
              pointerEvents: 'none',
              color: rucksack.palette.text.secondary,
            }}
          >
            Max file size: {maxDocumentUploadSizeMB} MB
          </Typography>
        </Box>
      )}
    </Box>
  )
}

interface InlineFileUploaderProps {
  documentId: number
  documentName: string
}
